import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, inject, Input, signal, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { CoreService } from 'src/app/services/core.service';
import { AppSettings } from 'src/app/config';
import { filter } from 'rxjs/operators';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AppNavItemComponent } from './vertical/sidebar/nav-item/nav-item.component';
import { RouterModule } from '@angular/router';
import { MaterialModule } from 'src/app/material.module';
import { CommonModule } from '@angular/common';
import { SidebarComponent } from './vertical/sidebar/sidebar.component';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { TablerIconsModule } from 'angular-tabler-icons';
import { HeaderComponent } from './vertical/header/header.component';
import { AppBreadcrumbComponent } from './shared/breadcrumb/breadcrumb.component';
import { CustomizerComponent } from './shared/customizer/customizer.component';
import { ScrollToTopComponent } from 'src/app/components/scroll-to-top/scroll-to-top.component';
import { AskPanelComponent } from './vertical/header/ask-panel/ask-panel.component';
import { TranslateModule } from '@ngx-translate/core';
import { UserAuthService } from 'src/app/services/user-auth.service';

const MONITOR_VIEW = 'screen and (min-width: 1024px)';
const BELOWMONITOR = 'screen and (max-width: 1023px)';

// for mobile app sidebar
export interface App {
  id: number;
  img: string;
  title: string;
  subtitle: string;
  link: string;
}

interface quicklinks {
  id: number;
  title: string;
  link: string;
}

@Component({
  selector: 'app-full',
  imports: [
    RouterModule,
    AppNavItemComponent,
    MaterialModule,
    CommonModule,
    SidebarComponent,
    NgScrollbarModule,
    TablerIconsModule,
    HeaderComponent,
    AppBreadcrumbComponent,
    CustomizerComponent,
    TranslateModule,
    ScrollToTopComponent,
    AskPanelComponent,
  ],
  templateUrl: './full.component.html',
  styleUrls: [],
  encapsulation: ViewEncapsulation.None,
})
export class FullComponent {
  userAuthService = inject(UserAuthService);
  navItems = this.userAuthService.menuItems;

  @ViewChild('leftsidenav')
  public sidenav: MatSidenav;
  @ViewChild('content', { static: true }) content!: MatSidenavContent;
  //get options from service
  options = this.settings.getOptions();
  private layoutChangesSubscription = Subscription.EMPTY;
  private isMobileScreen = false;
  private isContentWidthFixed = true;
  private isCollapsedWidthFixed = false;
  private htmlElement!: HTMLHtmlElement;

  @Input() homePage: boolean = false;

  get isOver(): boolean {
    return this.isMobileScreen;
  }

  get sidenavMode(): 'over' | 'side' {
    return this.isOver ? 'over' : 'side';
  }

  get showDesktopToggle(): boolean {
    return !this.isOver;
  }

  get isSidenavOpened(): boolean {
    if (this.options.navPos !== 'side') {
      return false;
    }

    return this.isOver ? this.options.sidenavOpened : true;
  }

  // Ask help panel: `askPanelInitialized` latches on first open so the panel is built
  // lazily, but survives closing (and keeps its results) afterwards.
  askPanelOpened = signal(false);
  askPanelInitialized = signal(false);

  // Build and start fetching as the panel begins opening, not once the animation ends.
  onAskPanelOpenedStart(): void {
    this.askPanelInitialized.set(true);
    this.askPanelOpened.set(true);
  }

  onAskPanelOpenedChange(opened: boolean): void {
    this.askPanelOpened.set(opened);
  }

  // for mobile app sidebar
  apps = signal<App[]>([]);

  quicklinks: quicklinks[] = [];

  constructor(
    private settings: CoreService,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private route: ActivatedRoute
  ) {
    this.htmlElement = document.querySelector('html')!;
    this.layoutChangesSubscription = this.breakpointObserver
      .observe([MONITOR_VIEW, BELOWMONITOR])
      .subscribe((state) => {
        const isOverlayMode = state.breakpoints[BELOWMONITOR];
        const modeChanged = this.isMobileScreen !== isOverlayMode;

        this.isMobileScreen = isOverlayMode;

        if (modeChanged) {
          this.options.sidenavOpened = !isOverlayMode;
          this.settings.setOptions(this.options);
        }

        this.isContentWidthFixed = state.breakpoints[MONITOR_VIEW];
      });

    // Initialize project theme with options
    this.receiveOptions(this.options);

    // This is for scroll to top
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((e) => {
      this.content.scrollTo({ top: 0 });
    });

    // this.route.firstChild?.data.subscribe((data) => {
    //   console.log('data from child route:', data);
    //   this.homePage = data['homePage'] || false;
    // });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      const child = this.getDeepestChild(this.route);
      this.homePage = child.snapshot.data['homePage'] || false;
      console.log('homePage updated:', this.homePage);
    });
  }

  private getDeepestChild(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  ngOnDestroy() {
    this.layoutChangesSubscription.unsubscribe();
  }

  toggleCollapsed() {
    this.isContentWidthFixed = false;
    this.options.sidenavCollapsed = !this.options.sidenavCollapsed;
    this.resetCollapsedState();
  }

  resetCollapsedState(timer = 400) {
    setTimeout(() => this.settings.setOptions(this.options), timer);
  }

  onSidenavClosedStart() {
    this.isContentWidthFixed = false;
  }

  onSidenavOpenedChange(isOpened: boolean) {
    this.isCollapsedWidthFixed = !this.isOver;
    this.options.sidenavOpened = isOpened;
    this.settings.setOptions(this.options);
  }

  receiveOptions(options: AppSettings): void {
    this.options = options;
    this.toggleDarkTheme(options);
  }

  toggleDarkTheme(options: AppSettings) {
    if (options.theme === 'dark') {
      this.htmlElement.classList.add('dark-theme');
      this.htmlElement.classList.remove('light-theme');
    } else {
      this.htmlElement.classList.remove('dark-theme');
      this.htmlElement.classList.add('light-theme');
    }
  }
}
